import { useState } from 'react';
import { checkoutSelectionFromUrl } from '../api/subscriptions';
import AuthLayout from '../components/AuthLayout';
import AppButton from '../components/common/AppButton/AppButton';
import SubscriptionBillingActions from '../components/subscription/SubscriptionBillingActions';
import type { SubscriptionPeriod } from '../components/subscription/SubscriptionCard';
import SubscriptionSelector from '../components/subscription/SubscriptionSelector';
import { plans } from '../config/subscriptionPlans';
import { useAuth } from '../context/AuthContext';
import { useSignOut } from '../hooks/useSignOut';
import { useSubscriptionCheckout } from '../hooks/useSubscriptionCheckout';
import type { PlanId } from '../types/subscription';

const SubscriptionExpiredPage = () => {
  const { hasPermission } = useAuth();
  const { signOut, signingOut } = useSignOut();
  const canManageSubscription = hasPermission('canManageSubscription');

  const [selectedSubsPeriod, setSelectedSubsPeriod] =
    useState<SubscriptionPeriod>(checkoutSelectionFromUrl().period);
  const [selectedPlanId, setSelectedPlanId] = useState<PlanId>(
    checkoutSelectionFromUrl().plan
  );

  const checkout = useSubscriptionCheckout({ returningSubscriber: true });
  const handleSubscribe = (plan: PlanId) => {
    setSelectedPlanId(plan);
    void checkout.subscribe(plan, selectedSubsPeriod);
  };

  const subtitle = (
    <h3 className="text-base font-normal text-dark">
      Choose a plan to restart your subscription. Have a promo code? Apply it at
      checkout.
    </h3>
  );

  return (
    <AuthLayout
      title="Subscription expired - Choose your plan"
      subtitle={subtitle}
      multiColumn={true}
    >
      <SubscriptionSelector
        plans={plans}
        period={selectedSubsPeriod}
        selectedPlanId={selectedPlanId}
        billingNote={
          selectedSubsPeriod === 'monthly'
            ? '{regularPrice}/month. Billed monthly.'
            : undefined
        }
        onSelectPlan={setSelectedPlanId}
        onSubscribe={handleSubscribe}
        onPeriodChange={setSelectedSubsPeriod}
        disabled={!canManageSubscription || checkout.busy}
        activeButtonLabel={checkout.buttonLabel}
        loading={checkout.busy}
        buttonLabel="CONTINUE"
      />

      {checkout.error && <p role="alert">{checkout.error}</p>}
      {checkout.cancellationPending && checkout.state === 'error' && (
        <AppButton onClick={checkout.retryCancellation}>
          Retry checkout cancellation
        </AppButton>
      )}
      <SubscriptionBillingActions disabled={checkout.busy} />

      {!canManageSubscription && (
        <p className="mt-6 text-center text-sm text-textLight">
          Only the account owner can manage this subscription. Contact them to
          renew.
        </p>
      )}

      <div className="mt-6">
        <AppButton
          type="button"
          fullWidth
          variant="tertiary"
          loading={signingOut}
          disabled={signingOut || checkout.state === 'checkout'}
          onClick={signOut}
        >
          Log out
        </AppButton>
      </div>
    </AuthLayout>
  );
};

export default SubscriptionExpiredPage;
